import React, { useEffect, useState } from 'react';
import { X, Loader2, Building2 } from 'lucide-react';

export interface NovaEscolaForm {
  cnpj: string;
  razaoSocial: string;
  nomeFantasia: string;
  email: string;
  telefone: string;
  cep: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  site: string;
  gestorNome: string;
  gestorEmail: string;
}

interface NovaEscolaModalProps {
  aberto: boolean;
  salvando: boolean;
  erro: string;
  onFechar: () => void;
  onSalvar: (dados: NovaEscolaForm) => Promise<void>;
}

const formVazio = (): NovaEscolaForm => ({
  cnpj: '',
  razaoSocial: '',
  nomeFantasia: '',
  email: '',
  telefone: '',
  cep: '',
  logradouro: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  site: '',
  gestorNome: '',
  gestorEmail: '',
});

const soDigitos = (valor: string) => valor.replace(/\D/g, '');

const mascaraCnpj = (valor: string) => {
  const n = soDigitos(valor).slice(0, 14);
  return n
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
};

const mascaraTelefone = (valor: string) => {
  const n = soDigitos(valor).slice(0, 11);
  if (n.length <= 10) {
    return n
      .replace(/^(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  }
  return n
    .replace(/^(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d)/, '$1-$2');
};

const mascaraCep = (valor: string) => {
  const n = soDigitos(valor).slice(0, 8);
  return n.replace(/^(\d{5})(\d)/, '$1-$2');
};

const NovaEscolaModal: React.FC<NovaEscolaModalProps> = ({
  aberto,
  salvando,
  erro,
  onFechar,
  onSalvar,
}) => {
  const [form, setForm] = useState<NovaEscolaForm>(formVazio());
  const [erroLocal, setErroLocal] = useState('');

  useEffect(() => {
    if (aberto) {
      setForm(formVazio());
      setErroLocal('');
    }
  }, [aberto]);

  if (!aberto) return null;

  const atualizar = (campo: keyof NovaEscolaForm, valor: string) => {
    setForm((atual) => {
      const proximo = { ...atual, [campo]: valor };
      if (campo === 'email' && !atual.gestorEmail.trim()) {
        proximo.gestorEmail = valor;
      }
      return proximo;
    });
    setErroLocal('');
  };

  const validar = (): string => {
    if (soDigitos(form.cnpj).length !== 14) return 'Informe um CNPJ válido com 14 dígitos.';
    if (!form.razaoSocial.trim()) return 'Informe a razão social.';
    if (!form.nomeFantasia.trim()) return 'Informe o nome fantasia.';
    if (!form.email.trim() || !form.email.includes('@')) return 'Informe um e-mail válido da escola.';
    if (soDigitos(form.telefone).length < 10) return 'Informe o telefone com DDD.';
    if (soDigitos(form.cep).length !== 8) return 'Informe um CEP válido.';
    if (!form.logradouro.trim()) return 'Informe o logradouro.';
    if (!form.numero.trim()) return 'Informe o número.';
    if (!form.bairro.trim()) return 'Informe o bairro.';
    if (!form.cidade.trim()) return 'Informe a cidade.';
    if (!form.gestorNome.trim()) return 'Informe o nome do administrador do painel.';
    const emailAcesso = (form.gestorEmail || form.email).trim();
    if (!emailAcesso || !emailAcesso.includes('@')) return 'Informe o e-mail de acesso ao painel.';
    return '';
  };

  const enviar = async (evento: React.FormEvent) => {
    evento.preventDefault();
    const msg = validar();
    if (msg) {
      setErroLocal(msg);
      return;
    }
    await onSalvar({
      ...form,
      gestorEmail: (form.gestorEmail || form.email).trim(),
    });
  };

  const fechar = () => {
    if (salvando) return;
    setForm(formVazio());
    setErroLocal('');
    onFechar();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">Nova escola</h2>
          </div>
          <button
            type="button"
            onClick={fechar}
            disabled={salvando}
            className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={enviar} className="px-6 py-4 space-y-4">
          <p className="text-sm text-gray-500">
            Cadastro feito pela Victall. Ao salvar, a escola e o primeiro acesso ao painel são criados juntos.
          </p>

          {(erroLocal || erro) && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {erroLocal || erro}
            </div>
          )}

          <div className="grid gap-3 md:grid-cols-2">
            <label className="block md:col-span-2">
              <span className="text-xs font-medium text-gray-600">CNPJ</span>
              <input
                className="input mt-1"
                value={form.cnpj}
                onChange={(e) => atualizar('cnpj', mascaraCnpj(e.target.value))}
                placeholder="00.000.000/0000-00"
                inputMode="numeric"
                autoComplete="off"
                disabled={salvando}
              />
            </label>

            <label className="block md:col-span-2">
              <span className="text-xs font-medium text-gray-600">Razão social</span>
              <input
                className="input mt-1"
                value={form.razaoSocial}
                onChange={(e) => atualizar('razaoSocial', e.target.value)}
                disabled={salvando}
              />
            </label>

            <label className="block md:col-span-2">
              <span className="text-xs font-medium text-gray-600">Nome fantasia</span>
              <input
                className="input mt-1"
                value={form.nomeFantasia}
                onChange={(e) => atualizar('nomeFantasia', e.target.value)}
                disabled={salvando}
              />
            </label>

            <label className="block">
              <span className="text-xs font-medium text-gray-600">E-mail da escola</span>
              <input
                className="input mt-1"
                type="email"
                value={form.email}
                onChange={(e) => atualizar('email', e.target.value)}
                disabled={salvando}
              />
            </label>

            <label className="block">
              <span className="text-xs font-medium text-gray-600">Telefone</span>
              <input
                className="input mt-1"
                value={form.telefone}
                onChange={(e) => atualizar('telefone', mascaraTelefone(e.target.value))}
                placeholder="(00) 00000-0000"
                inputMode="numeric"
                disabled={salvando}
              />
            </label>

            <label className="block">
              <span className="text-xs font-medium text-gray-600">CEP</span>
              <input
                className="input mt-1"
                value={form.cep}
                onChange={(e) => atualizar('cep', mascaraCep(e.target.value))}
                placeholder="00000-000"
                inputMode="numeric"
                disabled={salvando}
              />
            </label>

            <label className="block">
              <span className="text-xs font-medium text-gray-600">Cidade</span>
              <input
                className="input mt-1"
                value={form.cidade}
                onChange={(e) => atualizar('cidade', e.target.value)}
                disabled={salvando}
              />
            </label>

            <label className="block md:col-span-2">
              <span className="text-xs font-medium text-gray-600">Logradouro</span>
              <input
                className="input mt-1"
                value={form.logradouro}
                onChange={(e) => atualizar('logradouro', e.target.value)}
                disabled={salvando}
              />
            </label>

            <label className="block">
              <span className="text-xs font-medium text-gray-600">Número</span>
              <input
                className="input mt-1"
                value={form.numero}
                onChange={(e) => atualizar('numero', e.target.value)}
                disabled={salvando}
              />
            </label>

            <label className="block">
              <span className="text-xs font-medium text-gray-600">Complemento</span>
              <input
                className="input mt-1"
                value={form.complemento}
                onChange={(e) => atualizar('complemento', e.target.value)}
                disabled={salvando}
              />
            </label>

            <label className="block">
              <span className="text-xs font-medium text-gray-600">Bairro</span>
              <input
                className="input mt-1"
                value={form.bairro}
                onChange={(e) => atualizar('bairro', e.target.value)}
                disabled={salvando}
              />
            </label>

            <label className="block">
              <span className="text-xs font-medium text-gray-600">Site (opcional)</span>
              <input
                className="input mt-1"
                value={form.site}
                onChange={(e) => atualizar('site', e.target.value)}
                disabled={salvando}
              />
            </label>
          </div>

          <div className="rounded-lg border border-primary-100 bg-primary-50/40 p-4 space-y-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Acesso ao painel da escola</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Será gerada uma senha temporária. Guarde-a — ela só aparece uma vez.
              </p>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <label className="block md:col-span-2">
                <span className="text-xs font-medium text-gray-600">Nome do administrador</span>
                <input
                  className="input mt-1"
                  value={form.gestorNome}
                  onChange={(e) => atualizar('gestorNome', e.target.value)}
                  disabled={salvando}
                />
              </label>
              <label className="block md:col-span-2">
                <span className="text-xs font-medium text-gray-600">E-mail de login</span>
                <input
                  className="input mt-1"
                  type="email"
                  value={form.gestorEmail}
                  onChange={(e) => atualizar('gestorEmail', e.target.value)}
                  placeholder="Usa o e-mail da escola se ficar em branco"
                  disabled={salvando}
                />
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={fechar}
              disabled={salvando}
              className="btn btn-outline px-4 py-2"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={salvando}
              className="btn btn-primary px-4 py-2 inline-flex items-center gap-2"
            >
              {salvando && <Loader2 className="h-4 w-4 animate-spin" />}
              {salvando ? 'Salvando' : 'Cadastrar escola'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NovaEscolaModal;
